import { Button } from "@mui/material"
import { useState } from "react"
import { Secure } from "lib/authorization/secure"
import { useDialog } from "lib/@hooks/useDialog"
import { errorSnackbar } from "lib/snackbar/error-snackbar"
import { successSnackbar } from "lib/snackbar/success-snackbar"
import Iconify from "minimals-template/components/Iconify"
import { createProject } from "authoring/routes/project/controller/create-project"
import { updateProject } from "authoring/routes/project/controller/update-project"
import { ProjectDialog } from "authoring/routes/project/components/project-dialog"
import { navigate } from "lib/routes/navigate"
import {
    getProjectBridgeCreateWorkPackageLabel,
    getProjectBridgeSelectedScheduleIds,
} from "./project-bridge-list-presenters"
import { clearProjectBridgeScheduleSelection, useProjectBridgeScheduleSelection } from "./project-bridge-selection"
import { AUTHORING_MANAGE_WORK_PACKAGES_DEMAND } from "authoring/lib/constants/authoring-permissions.mjs"

export function ProjectBridgeCreateWorkPackageButton({ items = [] }) {
    const openProjectDialog = useDialog(<ProjectDialog />, { maxWidth: "sm", fullWidth: true })
    const selectedScheduleIdsById = useProjectBridgeScheduleSelection()
    const [creatingWorkPackage, setCreatingWorkPackage] = useState(false)
    const selectedScheduleIds = getProjectBridgeSelectedScheduleIds(selectedScheduleIdsById, items)
    const selectedCount = selectedScheduleIds.length
    const createWorkPackageLabel = getProjectBridgeCreateWorkPackageLabel(selectedCount)

    return (
        <Secure demand={AUTHORING_MANAGE_WORK_PACKAGES_DEMAND} fallback={<></>}>
            <Button
                variant="contained"
                size="small"
                disabled={!selectedCount || creatingWorkPackage}
                onClick={handleCreateWorkPackage}
                startIcon={<Iconify icon="ic:round-add" />}
                data-cy="project-bridge-create-work-package-button"
            >
                {createWorkPackageLabel}
            </Button>
        </Secure>
    )

    async function handleCreateWorkPackage() {
        try {
            setCreatingWorkPackage(true)
            const project = await openProjectDialog()
            if (!project) return

            const newProject = await createProject({
                ...project,
                scheduleIds: selectedScheduleIds,
            })

            if (!newProject) {
                errorSnackbar("Failed to create work package")
                return
            }

            const scoped = await updateProject({
                id: newProject.id,
                project: {
                    id: newProject.id,
                    scopedScheduleIds: selectedScheduleIds,
                },
            })

            if (!scoped) {
                errorSnackbar("Work package created but selected schedules could not be saved")
                return
            }

            clearProjectBridgeScheduleSelection()
            successSnackbar(`${newProject?.name} successfully created`)
            navigate(`/authoring/projects/${newProject?.id}`)
        } catch (error) {
            errorSnackbar(error?.message || "Failed to create work package")
        } finally {
            setCreatingWorkPackage(false)
        }
    }
}
